import _ from 'lodash';
import {
  take,
  call,
  put,
  fork,
  cancel,
  cancelled,
  takeLatest,
  all,
  select
} from 'redux-saga/effects';
import {delay} from 'redux-saga';
import {push} from 'react-router-redux';
import {startSubmit, stopSubmit} from 'redux-form';

import {
  fetchMe,
  requestLogin,
  requestLogout,
  fetchUserByInvitationKey,
  acceptInvitationAndSetPassword,
  adminForgotPassword,
  adminForgotPasswordSet
} from 'sdk/auth';
import {ROUTER_LOGIN_URL, LOCATION_LIST_URL} from 'config/urls';
import history from 'config/history';

import {getErrors} from 'utils/errors';
import {
  storeAuthToken,
  removeAuthToken,
  getAuthToken
} from 'utils/localStorage';
import {reverse, buildUrlFromNextRedirect} from 'utils/urls';
import {request} from 'utils/sagas';

import {
  LOGIN_REQUEST,
  LOGIN_SUCCESS,
  LOGIN_ERROR,
  LOGOUT_REQUEST,
  USER_PROFILE_REQUEST,
  USER_PROFILE_SUCCESS,
  loginError,
  loginSuccess,
  logoutRequest,
  userProfileSuccess,
  fetchUserByInvitationKey as fetchUserByInvitationKeyAction,
  CHANGE_USER_PASSWORD_AND_LOGIN,
  changeUserPasswordAndLoginFailure,
  ADMIN_FORGOT_PASSWORD,
  adminForgotPasswordSuccess,
  adminForgotPasswordFailure,
  ADMIN_FORGOT_PASSWORD_SET,
  adminForgotPasswordSetSuccess,
  adminForgotPasswordSetFailure,
  loginRequest as loginRequestAction,
  requiresPermission,
  REQUIRES_PERMISSION,
  REQUIRES_PERMISSION_OR_TEAM_OWNERSHIP,
  REQUIRES_MULTIPLE_PERMISSIONS,
  START_INACTIVITY_LOGOUT_TIMER,
  startInactivityLogoutTimer,
  STOP_INACTIVITY_LOGOUT_TIMER,
  stopInactivityLogoutTimer,
  redirectToDefaultPage,
  REDIRECT_TO_DEFAULT_PAGE,
  PERMISSIONS
} from './';

import {BOOKING_SET_LIST_URL, FRONT_DESK_URL} from 'config/urls';

import {delayedAction} from 'ducks/common';
import {openNoPermissionModal} from 'ducks/modals';
import {
  selectCurrentUserPermissions,
  selectCurrentUserTeams,
  selectInactivityLogoutTimer
} from 'ducks/auth';
import {
  hasEntityPermission,
  hasEntityPermissionOrTeamOwnership
} from 'utils/permissions';
import {createFetchSagaLatest} from 'utils/sagas';

function* userProfileRequestWorker() {
  try {
    const {data} = yield call(fetchMe, {});

    yield put(userProfileSuccess(data));
  } catch (error) {
    if (!_.get(error, 'response.status')) {
      throw error;
    }

    if (_.get(error, 'response.status') === 403) {
      /*
        If we call `logout` here instead - we will redirect the user to
        the login page without preserving his requested url in the GET params.
        That is why we currently preserve the requested url manually.

        TODO: refactor this and extract it into an action that can be
        used in the LoginRequired HOC as well.
      */
      removeAuthToken();

      const loginUrl = `${ROUTER_LOGIN_URL}?_next=${
        history.location.pathname
      }${_.replace(history.location.search, '?', '&')}`;

      yield put(push(loginUrl));
    }
  }
}

function* changeUserPasswordAndLoginFlow(action) {
  yield put(startSubmit('acceptInvitation'));

  const {email, key, password, recaptcha} = action.payload;
  const {data, errors, notFound} = yield request(
    acceptInvitationAndSetPassword,
    {
      key,
      password
    }
  );

  if (data) {
    yield put(stopSubmit('acceptInvitation'));
    yield put(loginRequestAction({email, password, recaptcha}));
  }

  if (errors) {
    yield put(stopSubmit('acceptInvitation', errors));
    yield put(changeUserPasswordAndLoginFailure(errors));
  }

  if (notFound) {
    yield put(stopSubmit('acceptInvitation'));
  }
}

function* adminForgotPasswordWorker(action) {
  yield put(startSubmit('forgotPassword'));
  const {data, errors, notFound} = yield request(
    adminForgotPassword,
    action.payload
  );

  if (data) {
    yield put(adminForgotPasswordSuccess());
    yield put(stopSubmit('forgotPassword'));
  }

  if (errors) {
    yield put(adminForgotPasswordFailure(errors));
    yield put(stopSubmit('forgotPassword', errors));
  }
  if (notFound) {
    yield put(adminForgotPasswordFailure({_error: 'Email not found'}));
    yield put(stopSubmit('forgotPassword', {_error: 'Email not found'}));
  }
}

function* adminForgotPasswordSetWorker(action) {
  yield put(startSubmit('forgotPasswordSet'));
  const {data, errors, notFound} = yield request(
    adminForgotPasswordSet,
    action.payload
  );

  if (data) {
    yield put(adminForgotPasswordSetSuccess());
    yield put(stopSubmit('forgotPasswordSet'));
  }

  if (errors) {
    yield put(adminForgotPasswordSetFailure(errors));
    yield put(stopSubmit('forgotPasswordSet', errors));
  }
  if (notFound) {
    yield put(adminForgotPasswordSetFailure({_error: 'Invalid token'}));
    yield put(stopSubmit('forgotPasswordSet', {_error: 'Invalid token'}));
  }
}

function* requiresPermissionWorker(action) {
  const state = yield select();
  const userPermissions = selectCurrentUserPermissions(state);

  // If this is a race condition with fetching the userPermissions. Wait for the perimssions
  if (!userPermissions) {
    yield put(
      delayedAction({
        delay: 1000,
        action: () =>
          requiresPermission(action.payload, action.payload.hasPermissionFunc)
      })
    );
    return;
  }

  if (!hasEntityPermission({userPermissions, ...action.payload})) {
    yield put(openNoPermissionModal(action.payload));
  } else {
    yield call(action.payload.hasPermissionFunc);
  }
}

function* requiresMultiplePermissionWorker(action) {
  const state = yield select();
  const userPermissions = selectCurrentUserPermissions(state);
  const {permissions} = action.payload;

  const userCheckedPermissions = permissions.map(element => ({
    permission: element.permission,
    hasPermission: hasEntityPermission({userPermissions, ...element})
  }));

  const userNotAllowedPermissions = userCheckedPermissions.filter(
    element => !element.hasPermission
  );

  if (userNotAllowedPermissions.length === 0) {
    yield call(action.payload.hasPermissionFunc);
  } else {
    const missingPermissions = userNotAllowedPermissions
      .map(element => element.permission)
      .join(', ');

    const message = `Sorry, but you don't have the following permissions: ${missingPermissions}. Please contact your Line Manager for more details.`;
    yield put(
      openNoPermissionModal({...action.payload, noPermissionMessage: message})
    );
  }
}

function* requiresPermissionOrTeamOwnershipWorker(action) {
  let state = yield select();
  const userPermissions = selectCurrentUserPermissions(state);
  const userTeams = selectCurrentUserTeams(state);

  const hasPermission = hasEntityPermissionOrTeamOwnership({
    userPermissions,
    userTeams,
    ...action.payload
  });

  if (!hasPermission) {
    yield put(openNoPermissionModal(action.payload));
  } else {
    yield call(action.payload.hasPermissionFunc);
  }
}

function* redirectToDefaultPageWorker(action) {
  const {data, error} = yield request(fetchMe, undefined);

  const locationRef = action.payload.locationRef;

  const permissionsToCheck = [
    {permission: PERMISSIONS.VIEW_FRONT_DESK_DASHBOARD, url: FRONT_DESK_URL},
    {permission: '', url: BOOKING_SET_LIST_URL}
  ];

  if (data) {
    const page = _.first(
      _.dropWhile(permissionsToCheck, ({permission}) => {
        if (permission == '') return false;

        return !data.permissions.location[locationRef][permission];
      })
    );

    yield put(push(reverse(_.get(page, 'url'), {locationRef})));
  }

  if (error) {
    throw error;
  }
}

function* userProfileRequestSaga() {
  yield all([
    takeLatest(USER_PROFILE_REQUEST, userProfileRequestWorker),
    createFetchSagaLatest(
      fetchUserByInvitationKeyAction,
      fetchUserByInvitationKey
    ),
    takeLatest(CHANGE_USER_PASSWORD_AND_LOGIN, changeUserPasswordAndLoginFlow),
    takeLatest(ADMIN_FORGOT_PASSWORD, adminForgotPasswordWorker),
    takeLatest(ADMIN_FORGOT_PASSWORD_SET, adminForgotPasswordSetWorker),
    takeLatest(REQUIRES_PERMISSION, requiresPermissionWorker),
    takeLatest(
      REQUIRES_PERMISSION_OR_TEAM_OWNERSHIP,
      requiresPermissionOrTeamOwnershipWorker
    ),
    takeLatest(REQUIRES_MULTIPLE_PERMISSIONS, requiresMultiplePermissionWorker),
    takeLatest(REDIRECT_TO_DEFAULT_PAGE, redirectToDefaultPageWorker)
  ]);
}

function* inactivityLogoutTimer(forceLogoutAfter) {
  let currentLogoutTimer = 0;

  const resetTimer = _.debounce(() => {
    currentLogoutTimer = 0;
  }, 200);

  // In future, think of another events if these are not enough.
  // For example, if a team uses a tablet add a `touchstart` event.
  const interactionEvents = ['mousemove', 'keypress', 'scroll'];

  interactionEvents.forEach(event =>
    document.addEventListener(event, resetTimer)
  );

  try {
    while (currentLogoutTimer < forceLogoutAfter) {
      yield call(delay, 60 * 1000); // one minute
      currentLogoutTimer += 1;
    }

    yield put(stopInactivityLogoutTimer());
  } finally {
    // We remove the eventListener in `finally` as the inactivity logout timer
    // can be interrupted due to a premature logout request.
    interactionEvents.forEach(event =>
      document.removeEventListener(event, resetTimer)
    );
  }
}

function* forceInactivityLogoutFlow() {
  while (true) {
    let action: any = null;
    let timer: any = null;

    action = yield take(START_INACTIVITY_LOGOUT_TIMER);
    const forceLogoutAfter = action.payload.forceLogoutAfter;
    timer = yield fork(inactivityLogoutTimer, forceLogoutAfter);

    action = yield take([LOGOUT_REQUEST, STOP_INACTIVITY_LOGOUT_TIMER]);

    if (action.type === LOGOUT_REQUEST) {
      yield cancel(timer);
    }

    if (action.type === STOP_INACTIVITY_LOGOUT_TIMER) {
      yield put(
        logoutRequest(
          `You have been logged out due to inactivity of ${forceLogoutAfter} minutes.`
        )
      );
    }
  }
}

function* authenticate(credentials) {
  try {
    yield put(startSubmit('login'));
    const {data} = yield call(requestLogin, credentials);

    storeAuthToken(data.token);

    // We redirect to Locations list page by default
    let redirectUrl = LOCATION_LIST_URL;

    // If the user had come from another page we want to redirect him back to it.
    // We store the previous location in the `_next=` GET parameter.
    // If such parameter exists, we build the new url and use it for the redirect.
    const originalUrl = buildUrlFromNextRedirect(history.location.search);
    if (!_.isEmpty(originalUrl)) {
      redirectUrl = originalUrl;
    }

    const forceLogoutAfter = selectInactivityLogoutTimer(data);

    if (forceLogoutAfter) {
      yield put(startInactivityLogoutTimer(forceLogoutAfter));
    }

    // TODO: refactor
    const userResponse = yield request(fetchMe, undefined);
    const locationsMapped = _.map(
      userResponse.data.permissions.location,
      (locationPermissions, locationRef) => ({
        locationRef,
        isAllowed: locationPermissions[PERMISSIONS.VIEW_LOCATION]
      })
    );

    const locationsWithPermission = locationsMapped
      .filter(({isAllowed}) => isAllowed)
      .map(({locationRef}) => locationRef);

    yield* [
      put(stopSubmit('login')),
      put(loginSuccess()),
      put(userProfileSuccess(data.me))
    ];

    // This introduces a bug with not redirecting to `originalUrl` if there is any and locations.length === 1
    if (locationsWithPermission.length === 1) {
      yield put(
        redirectToDefaultPage({locationRef: locationsWithPermission[0]})
      );
    } else {
      yield put(push(reverse(redirectUrl, {})));
    }
  } catch (error) {
    let errors;

    if (!_.get(error, 'response.status')) {
      throw error;
    }

    if (_.get(error, 'response.status') === 400) {
      errors = getErrors(error.response.data);
    }

    yield* [put(stopSubmit('login', errors)), put(loginError(error))];
  } finally {
    if (yield cancelled()) {
      yield put(push(ROUTER_LOGIN_URL));
    }
  }
}

function* logout() {
  const authToken = getAuthToken();

  if (authToken) {
    try {
      yield call(requestLogout);
    } catch (error) {
      // Since we can get a 403 from the logout API, we do nothing with the error.
      // Perhaps the API should always return a success?
    } finally {
      // We move the `removeAuth` function call here because we want no matter what
      // happens with the `requestLogout` call to remove the `authToken` form localStorage
      // a.k.a. de-authenticate the user.
      removeAuthToken();
      yield put(push(ROUTER_LOGIN_URL));
    }
  }
}

function* loginFlow() {
  while (true) {
    let authenticateTask: any = null;
    let action: any = null;

    action = yield take([LOGIN_REQUEST, USER_PROFILE_SUCCESS]);

    if (action.type === LOGIN_REQUEST) {
      authenticateTask = yield fork(authenticate, action.payload);

      action = yield take([LOGIN_SUCCESS, LOGIN_ERROR]);

      if (action.type === LOGIN_ERROR) {
        continue;
      }
    }

    action = yield take(LOGOUT_REQUEST);

    // If the user is trying to log out before logging in is finished we cancel the authentication.
    // This is a very rare race condition that's hard to reproduce, but still, we handle that.
    // Check https://redux-saga.js.org/docs/advanced/NonBlockingCalls.html for more info.
    if (authenticateTask) {
      yield cancel(authenticateTask);
    }

    yield call(logout);
  }
}

export {loginFlow, forceInactivityLogoutFlow, userProfileRequestSaga};
